import { useState, useEffect, useCallback } from 'react';
import { API_BASE } from '../../utils/api';

export interface MetricsSummary {
  task_quality_score: number;
  task_success_rate: number;
  output_quality_avg: number;
  accuracy_score: number;
  hallucination_rate: number;
  grounding_score_avg: number;
  ground_truth_accuracy: number;
  total_requests: number;
  successful_requests: number;
  failed_requests: number;
  avg_latency_ms: number;
  p95_latency_ms: number;
  quality_evaluated_count: number;
  hallucination_checked_count: number;
  ground_truth_checked_count: number;
  time_range_minutes: number;
  generated_at: string;
}

export interface ExecutionLog {
  id: string;
  timestamp: string;
  agent: string;
  user: string;
  status: 'success' | 'failed';
  latency: number;
  tokens: number;
  task_success?: boolean;
  quality_score?: number;
  grounding_score?: number;
  trace_id?: string;
  user_query?: string;      // Input query preview
  agent_response?: string;  // Output response preview
  prompt?: string;          // Fallback for input
  response?: string;        // Fallback for output
}

export interface MetricsLogsResponse {
  logs: ExecutionLog[];
  total_count: number;
  page: number;
  page_size: number;
}

// Chart data types
export interface AgentBreakdownItem {
  agent_id: string;
  agent_name: string;
  request_count: number;
  success_rate: number;
  avg_quality: number;
  avg_latency: number;
}

export interface AgentBreakdownResponse {
  agents: AgentBreakdownItem[];
  total_requests: number;
}

export interface QualityDimension {
  dimension: string;
  label: string;
  score: number;
  count: number;
  description: string;
}

export interface QualityDimensionsResponse {
  dimensions: QualityDimension[];
}

export interface TrendDataPoint {
  timestamp: string;
  label: string;
  quality_score: number;
  success_rate: number;
  accuracy_score: number;
  request_count: number;
}

export interface TrendResponse {
  data: TrendDataPoint[];
  interval: string;
}

export interface EvaluationDetailItem {
  id: string;
  timestamp: string;
  agent: string;
  user_query: string;
  agent_response: string;
  task_success: boolean;
  quality_score?: number;
  quality_reasoning?: string;
  quality_dimensions?: {
    relevance?: number;
    coherence?: number;
    completeness?: number;
    helpfulness?: number;
  };
  grounding_score?: number;
  grounding_reasoning?: string;
  hallucination_rate?: number;  // 0-100%
  ground_truth_match?: number;
  latency_ms: number;
}

export interface EvaluationDetailsResponse {
  evaluations: EvaluationDetailItem[];
  total_count: number;
  page: number;
  page_size: number;
}

export function useMetrics(pollInterval: number = 5000) {
  const [metrics, setMetrics] = useState<MetricsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchMetrics = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/eval/realtime`);
      if (!response.ok) throw new Error('Failed to fetch metrics');
      const data = await response.json();
      setMetrics(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMetrics();
    const interval = setInterval(fetchMetrics, pollInterval);
    return () => clearInterval(interval);
  }, [fetchMetrics, pollInterval]);

  return { metrics, loading, error, refetch: fetchMetrics };
}

export function useLogs(page: number = 1, pageSize: number = 20, pollInterval: number = 10000) {
  const [logs, setLogs] = useState<MetricsLogsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchLogs = useCallback(async () => {
    try {
      const response = await fetch(
        `${API_BASE}/api/eval/logs?page=${page}&page_size=${pageSize}`
      );
      if (!response.ok) throw new Error('Failed to fetch logs');
      const data = await response.json();
      setLogs(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize]);

  useEffect(() => {
    fetchLogs();
    const interval = setInterval(fetchLogs, pollInterval);
    return () => clearInterval(interval);
  }, [fetchLogs, pollInterval]);

  return { logs, loading, error, refetch: fetchLogs };
}

// Chart data hooks
export function useAgentBreakdown(pollInterval: number = 30000) {
  const [data, setData] = useState<AgentBreakdownResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/eval/chart/agent-breakdown`);
      if (!response.ok) throw new Error('Failed to fetch agent breakdown');
      const result = await response.json();
      setData(result);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, pollInterval);
    return () => clearInterval(interval);
  }, [fetchData, pollInterval]);

  return { data, loading, error, refetch: fetchData };
}

export function useQualityDimensions(pollInterval: number = 30000) {
  const [data, setData] = useState<QualityDimensionsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/eval/chart/quality-dimensions`);
      if (!response.ok) throw new Error('Failed to fetch quality dimensions');
      const result = await response.json();
      setData(result);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, pollInterval);
    return () => clearInterval(interval);
  }, [fetchData, pollInterval]);

  return { data, loading, error, refetch: fetchData };
}

export function useTrendData(interval: string = 'day', days: number = 7, pollInterval: number = 60000) {
  const [data, setData] = useState<TrendResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/eval/chart/trend?interval=${interval}&days=${days}`);
      if (!response.ok) throw new Error('Failed to fetch trend data');
      const result = await response.json();
      setData(result);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [interval, days]);

  useEffect(() => {
    fetchData();
    const pollTimer = setInterval(fetchData, pollInterval);
    return () => clearInterval(pollTimer);
  }, [fetchData, pollInterval]);

  return { data, loading, error, refetch: fetchData };
}

export function useEvaluationDetails(page: number = 1, pageSize: number = 10, pollInterval: number = 30000) {
  const [data, setData] = useState<EvaluationDetailsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/eval/evaluation-details?page=${page}&page_size=${pageSize}`);
      if (!response.ok) throw new Error('Failed to fetch evaluation details');
      const result = await response.json();
      setData(result);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize]);

  useEffect(() => {
    fetchData();
    const pollTimer = setInterval(fetchData, pollInterval);
    return () => clearInterval(pollTimer);
  }, [fetchData, pollInterval]);

  return { data, loading, error, refetch: fetchData };
}
